import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isNativePlatform } from '@/services/platformService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Crown, Clock, XCircle, Mail, ArrowLeft, Globe } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import LanguageSelector from '@/components/LanguageSelector';

import logoDorado from '@/assets/Logo_Dorado.png';
import goldenButton from '@/assets/Golden_Button.png';

const ParticleBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = window.innerWidth;
    let h = window.innerHeight;
    let particles: { x: number; y: number; r: number; vx: number; vy: number; alpha: number; gold: boolean }[] = [];
    let animationFrameId: number;

    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };

    const createParticle = (resetY = false) => ({
      x: Math.random() * w,
      y: resetY ? h + 5 : Math.random() * h,
      r: Math.random() * 1.2 + 0.3,
      vx: (Math.random() - 0.5) * 0.15,
      vy: -Math.random() * 0.3 - 0.1,
      alpha: Math.random() * 0.4 + 0.1,
      gold: Math.random() > 0.5
    });

    const init = () => {
      resize();
      particles = Array.from({ length: 80 }, () => createParticle(false));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.y < -5 || p.x < -5 || p.x > w + 5) {
          particles[i] = createParticle(true);
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.gold
          ? `rgba(201, 152, 42, ${p.alpha})`
          : `rgba(240, 237, 230, ${p.alpha * 0.5})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(draw);
    };

    init();
    draw();

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none opacity-80"
    />
  );
};

const AmbassadorLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signIn, signInWithGoogle, signInWithApple, signInWithMicrosoft, signInWithTwitter, signInWithDiscord, signInWithFacebook, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [applicationStatus, setApplicationStatus] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    if (isNativePlatform()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!user) {
      setChecking(false);
      return;
    }
    const checkApplication = async () => {
      setChecking(true);
      const { data } = await supabase
        .from('ambassador_applications')
        .select('status')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!data) {
        navigate('/ambassador-onboard', { replace: true });
      } else if (data.status === 'approved') {
        navigate('/ambassador-dashboard', { replace: true });
      } else {
        setApplicationStatus(data.status);
      }
      setChecking(false);
    };
    checkApplication();
  }, [user, navigate]);

  if (isNativePlatform()) return null;

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    try {
      await signIn(email, password);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'destructive',
      });
    }
    setEmailLoading(false);
  };

  const StatusDisplay = () => {
    if (!applicationStatus || applicationStatus === 'approved') return null;

    const config: Record<string, { icon: React.ReactNode; borderColor: string; bgColor: string; iconGlow: string }> = {
      pending_verification: {
        icon: <Clock className="h-8 w-8 text-[#C9982A]" />,
        borderColor: 'border-[#C9982A]/30',
        bgColor: 'bg-[#C9982A]/5',
        iconGlow: 'shadow-[0_0_30px_rgba(201,152,42,0.15)]',
      },
      rejected: {
        icon: <XCircle className="h-8 w-8 text-red-400" />,
        borderColor: 'border-red-500/30',
        bgColor: 'bg-red-500/5',
        iconGlow: '',
      },
      suspended: {
        icon: <XCircle className="h-8 w-8 text-orange-400" />,
        borderColor: 'border-orange-500/30',
        bgColor: 'bg-orange-500/5',
        iconGlow: '',
      },
    };

    const c = config[applicationStatus] || config.pending_verification;

    return (
      <div className="space-y-4 w-full">
        <div className={`rounded-2xl border ${c.borderColor} ${c.bgColor} p-8 text-center backdrop-blur-sm`}>
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-black/30 mb-5 ${c.iconGlow}`}>
            {c.icon}
          </div>
          <h2 className="font-serif text-2xl font-bold text-[#F0EDE6] mb-2 tracking-wide">
            {t(`ambassador.login.status.${applicationStatus}.title`)}
          </h2>
          <p className="text-[#8E8A80] text-sm leading-relaxed">
            {t(`ambassador.login.status.${applicationStatus}.description`)}
          </p>
        </div>

        <div className="text-center">
          <a
            href="/ambassador-terms"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#C9982A]/70 hover:text-[#E8C46A] text-xs transition-colors underline underline-offset-2"
          >
            {t('ambassador.login.viewTerms', 'Review Terms & Conditions')}
          </a>
        </div>
      </div>
    );
  };

  const OAuthButton = ({ onClick, icon, label, className }: { onClick: () => void; icon: React.ReactNode; label: string; className: string }) => (
    <button
      onClick={onClick}
      disabled={authLoading}
      className={`relative w-full h-12 flex items-center justify-center gap-2.5 rounded-xl text-sm font-normal transition-all duration-200 overflow-hidden hover:-translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed ${className} group`}
    >
      <div className="absolute inset-0 bg-transparent group-hover:bg-white/5 transition-colors duration-200 pointer-events-none" />
      {icon}
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-[#F0EDE6] font-sans font-light relative overflow-hidden flex">
      {/* Floating Particles */}
      <ParticleBackground />
      {/* Dynamic Noise Overlay */}
      <div
        className="fixed inset-0 z-[1] w-full h-full opacity-[0.8] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }}
      />
      {/* Background Ambience Left */}
      <div className="fixed top-[-20%] left-[-10%] w-[70vw] h-[70vw] rounded-full bg-[radial-gradient(circle,rgba(201,152,42,0.06)_0%,transparent_65%)] pointer-events-none z-0" />
      {/* Background Ambience Right */}
      <div className="fixed bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-[radial-gradient(circle,rgba(100,60,180,0.04)_0%,transparent_65%)] pointer-events-none z-0" />

      {/* Main Container */}
      <div className="relative z-10 w-full min-h-screen grid lg:grid-cols-[1fr_480px] xl:grid-cols-[1fr_520px]">

        {/* === LEFT PANEL === */}
        <div className="hidden lg:flex flex-col justify-between px-14 py-16 border-r border-[#C9982A]/15 relative overflow-hidden">

          {/* Brand */}
          <div className="animate-fade-in">
            <img src={logoDorado} alt="Petius" className="h-12 w-auto" />
          </div>

          <div className="flex-1 flex flex-col justify-center py-16">
            {/* Headline Block */}
            <div className="animate-fade-in" style={{ animationDelay: '150ms', animationFillMode: 'both' }}>
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#C9982A] mb-6 flex items-center gap-3">
                <span className="inline-block w-8 h-[0.5px] bg-[#C9982A] opacity-60"></span>
                {t('ambassador.login.exclusive', 'Ambassador Program')}
              </p>
              <h1 className="font-serif text-5xl xl:text-6xl font-bold leading-[1.1] tracking-tight mb-3 text-transparent bg-clip-text bg-gradient-to-br from-[#F0EDE6] via-[#E8C46A] to-[#8B6514]">
                Shape the<br /><em className="font-normal italic">Future</em><br />of Petius
              </h1>
              <p className="font-serif italic text-xl font-light text-[#8E8A80] leading-relaxed max-w-[420px] mt-5">
                {t('ambassador.login.programSubtitle', 'Join an exclusive circle of visionaries who represent our brand with distinction.')}
              </p>
            </div>

            {/* Testimonial */}
            <div className="bg-white/[0.03] border border-[#C9982A]/15 rounded-2xl p-7 mt-12 animate-fade-in" style={{ animationDelay: '450ms', animationFillMode: 'both' }}>
              <p className="font-serif text-lg italic font-light text-[#C0BAB0] leading-relaxed">
                "{t('ambassador.login.testimonial.quote', 'Becoming a Petius Ambassador opened doors I never imagined — an authentic community and real impact.')}"
              </p>
              <div className="flex items-center gap-3 mt-5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9982A] to-[#8B6514] flex items-center justify-center text-[13px] font-medium text-[#1a1200] shrink-0">
                  SR
                </div>
                <div>
                  <p className="text-[13px] font-medium text-[#F0EDE6]">
                    {t('ambassador.login.testimonial.name', 'Sofia Reyes')}
                  </p>
                  <p className="text-[11px] text-[#4A4840] tracking-wider mt-0.5 uppercase">
                    {t('ambassador.login.testimonial.role', 'Top Ambassador · Mexico City')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            {/* Stats Row */}
            <div className="flex gap-10 pt-10 border-t border-[#C9982A]/15 animate-fade-in" style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
              <div>
                <div className="font-serif text-4xl font-semibold text-[#E8C46A] leading-none tracking-tight">2.4K</div>
                <div className="text-[11px] tracking-[0.12em] uppercase text-[#4A4840] mt-1.5">{t('ambassador.login.stats.ambassadors', 'Ambassadors')}</div>
              </div>
              <div>
                <div className="font-serif text-4xl font-semibold text-[#E8C46A] leading-none tracking-tight">48</div>
                <div className="text-[11px] tracking-[0.12em] uppercase text-[#4A4840] mt-1.5">{t('ambassador.login.stats.countries', 'Countries')}</div>
              </div>
              <div>
                <div className="font-serif text-4xl font-semibold text-[#E8C46A] leading-none tracking-tight">$1.2M</div>
                <div className="text-[11px] tracking-[0.12em] uppercase text-[#4A4840] mt-1.5">{t('ambassador.login.stats.paid', 'Commissions Paid')}</div>
              </div>
            </div>

            {/* Benefits */}
            <div className="flex flex-col gap-4 mt-9 animate-fade-in" style={{ animationDelay: '600ms', animationFillMode: 'both' }}>
              <div className="flex items-start gap-3.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C9982A] mt-2 shrink-0 shadow-[0_0_8px_rgba(201,152,42,0.5)]" />
                <p className="text-sm text-[#8E8A80] leading-relaxed">
                  <strong className="text-[#F0EDE6] font-medium mr-1">{t('ambassador.login.benefit1.strong', 'Exclusive commissions')}</strong>
                  {t('ambassador.login.benefit1.text', 'on every referral you bring')}
                </p>
              </div>
              <div className="flex items-start gap-3.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C9982A] mt-2 shrink-0 shadow-[0_0_8px_rgba(201,152,42,0.5)]" />
                <p className="text-sm text-[#8E8A80] leading-relaxed">
                  <strong className="text-[#F0EDE6] font-medium mr-1">{t('ambassador.login.benefit2.strong', 'Early access')}</strong>
                  {t('ambassador.login.benefit2.text', 'to new features before public launch')}
                </p>
              </div>
              <div className="flex items-start gap-3.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C9982A] mt-2 shrink-0 shadow-[0_0_8px_rgba(201,152,42,0.5)]" />
                <p className="text-sm text-[#8E8A80] leading-relaxed">
                  <strong className="text-[#F0EDE6] font-medium mr-1">{t('ambassador.login.benefit3.strong', 'Private community')}</strong>
                  {t('ambassador.login.benefit3.text', 'of top-tier brand advocates worldwide')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* === RIGHT PANEL (Auth Area) === */}
        <div className="flex flex-col justify-center px-6 sm:px-12 py-12 relative bg-gradient-to-b from-[#0F0F1A]/80 to-[#0A0A12]/95 border-l border-[#C9982A]/15">
          {/* Language Selector overlaying top right */}
          <div className="absolute top-6 right-6 z-20">
            <LanguageSelector variant="compact" className="bg-white/5 border-white/10 hover:border-[#C9982A]/30 text-[#8E8A80] hover:text-[#C0BAB0] shadow-sm backdrop-blur-md rounded-lg" />
          </div>

          <div className="w-full max-w-[360px] mx-auto">
            {/* Golden Button Badge */}
            <div className="flex justify-center mb-8 animate-fade-in">
              <div className="relative w-[110px] h-[110px] flex items-center justify-center">
                <div className="absolute -inset-2 rounded-full border border-[#C9982A]/30 animate-[ping_3s_ease-in-out_infinite]" />
                <div className="absolute -inset-6 rounded-full border border-[#C9982A]/10 animate-[ping_3s_1s_ease-in-out_infinite]" />

                <div className="relative z-10 w-full h-full flex items-center justify-center scale-110">
                  <img
                    src={goldenButton}
                    alt="Petius Badge"
                    className="w-full h-auto drop-shadow-[0_0_20px_rgba(201,152,42,0.3)]"
                  />
                </div>
              </div>
            </div>

            {/* Header */}
            <div className="text-center mb-8 animate-fade-in" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#C9982A] mb-2.5">
                {t('ambassador.login.exclusive', 'Exclusive Access')}
              </p>
              <h2 className="font-serif text-3xl font-bold text-[#F0EDE6] leading-[1.15]">
                {t('ambassador.login.title', 'Ambassador Portal')}
              </h2>
              <p className="font-serif italic text-base font-light text-[#8E8A80] mt-2">
                {t('ambassador.login.subtitle', 'Sign in to access your dashboard')}
              </p>
            </div>

            {checking ? (
              <div className="flex justify-center mt-10">
                <div className="w-10 h-10 border-[3px] border-[#C9982A]/20 border-t-[#C9982A] rounded-full animate-spin" />
              </div>
            ) : user && applicationStatus && applicationStatus !== 'approved' ? (
              <StatusDisplay />
            ) : !user ? (
              <div className="animate-fade-in" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
                <div className="bg-white/[0.03] backdrop-blur-md border border-[#C9982A]/15 rounded-[20px] p-6 lg:p-7 shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
                  {!showEmailForm ? (
                    <div className="space-y-2">
                      <OAuthButton
                        onClick={signInWithGoogle}
                        className="bg-white text-[#1a1a1a] font-medium"
                        icon={
                          <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          </svg>
                        }
                        label={t('ambassador.login.google', 'Continue with Google')}
                      />

                      <OAuthButton
                        onClick={signInWithApple}
                        className="bg-[#0a0a0a] text-white border border-white/10"
                        icon={
                          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                          </svg>
                        }
                        label={t('ambassador.login.apple', 'Continue with Apple')}
                      />

                      <OAuthButton
                        onClick={signInWithMicrosoft}
                        className="bg-[#1a1a1a] text-white border border-white/10"
                        icon={
                          <svg className="w-4 h-4" viewBox="0 0 21 21">
                            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                          </svg>
                        }
                        label={t('ambassador.login.microsoft', 'Continue with Microsoft')}
                      />

                      <OAuthButton
                        onClick={signInWithTwitter}
                        className="bg-[#0a0a0a] text-white border border-white/10"
                        icon={
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                          </svg>
                        }
                        label={t('ambassador.login.twitter', 'Continue with X')}
                      />

                      <OAuthButton
                        onClick={signInWithDiscord}
                        className="bg-[#5865F2] text-white"
                        icon={
                          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
                          </svg>
                        }
                        label={t('ambassador.login.discord', 'Continue with Discord')}
                      />

                      <OAuthButton
                        onClick={signInWithFacebook}
                        className="bg-[#1877F2] text-white"
                        icon={
                          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        }
                        label={t('ambassador.login.facebook', 'Continue with Facebook')}
                      />

                      <div className="flex items-center gap-3 my-4">
                        <div className="flex-1 h-[0.5px] bg-[#C9982A]/15" />
                        <span className="text-[11px] text-[#4A4840] tracking-[0.1em] uppercase">
                          {t('ambassador.login.or', 'or')}
                        </span>
                        <div className="flex-1 h-[0.5px] bg-[#C9982A]/15" />
                      </div>

                      <button
                        onClick={() => setShowEmailForm(true)}
                        className="w-full h-12 flex items-center justify-center gap-2.5 rounded-xl border border-[#C9982A]/25 text-sm text-[#8E8A80] hover:text-[#F0EDE6] hover:border-[#C9982A]/50 hover:bg-[#C9982A]/5 transition-all duration-200"
                      >
                        <Mail className="w-[18px] h-[18px]" />
                        {t('ambassador.login.email', 'Continue with Email')}
                      </button>
                    </div>
                  ) : (
                    <div className="animate-fade-in" style={{ animationDuration: '300ms' }}>
                      <button
                        onClick={() => setShowEmailForm(false)}
                        className="flex items-center gap-1.5 text-[13px] text-[#8E8A80] hover:text-[#C0BAB0] transition-colors mb-6"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        {t('common.back', 'Back')}
                      </button>
                      <form onSubmit={handleEmailSignIn} className="space-y-4">
                        <div>
                          <Label className="block text-[12px] uppercase tracking-[0.08em] text-[#4A4840] mb-2 font-normal">
                            {t('auth.email', 'Email')}
                          </Label>
                          <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="h-12 bg-white/[0.03] border-[#C9982A]/20 text-[#F0EDE6] rounded-xl focus-visible:ring-1 focus-visible:ring-[#C9982A]/50 focus-visible:border-[#C9982A]/50 placeholder:text-[#4A4840]"
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                        <div>
                          <Label className="block text-[12px] uppercase tracking-[0.08em] text-[#4A4840] mb-2 font-normal">
                            {t('auth.password', 'Password')}
                          </Label>
                          <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="h-12 bg-white/[0.03] border-[#C9982A]/20 text-[#F0EDE6] rounded-xl focus-visible:ring-1 focus-visible:ring-[#C9982A]/50 focus-visible:border-[#C9982A]/50 placeholder:text-[#4A4840]"
                            placeholder="••••••••"
                            required
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={emailLoading || !email || !password}
                          className="w-full h-[50px] mt-2 rounded-xl bg-gradient-to-br from-[#C9982A] to-[#8B6514] text-[#0a0800] font-medium tracking-[0.05em] hover:opacity-90 hover:-translate-y-[1px] transition-all shadow-[0_0_24px_rgba(201,152,42,0.15)] hover:shadow-[0_0_36px_rgba(201,152,42,0.3)] relative overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />
                          <span className="relative z-10">{emailLoading ? t('common.processing', 'Processing...') : t('ambassador.login.signIn', 'Sign In')}</span>
                        </Button>
                      </form>
                    </div>
                  )}
                </div>

                <p className="text-center text-[11px] text-[#4A4840] leading-[1.6] mt-6 animate-fade-in" style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
                  {t('ambassador.login.terms', 'By continuing, you agree to our')} <br className="sm:hidden" />
                  <a href="/ambassador-terms" target="_blank" className="text-[#C9982A]/70 hover:text-[#E8C46A] underline underline-offset-2 transition-colors inline-block pt-1">
                    {t('ambassador.login.termsLink', 'Terms & Conditions and Privacy Policy')}
                  </a>
                </p>
              </div>
            ) : (
              <div className="flex justify-center mt-10">
                <div className="w-10 h-10 border-[3px] border-[#C9982A]/20 border-t-[#C9982A] rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbassadorLogin;
